import {
  any,
  array,
  coerce,
  create,
  defaulted,
  Describe,
  enums,
  object,
  optional,
  record,
  string,
  type,
} from 'superstruct';

import {
  FilterDto,
  FilterDtoValidator,
  makeFilterList,
  makeTabFilter,
} from '~modules/media-library/model/FilterFactory';
import { makeFilterOptionId } from '~modules/media-library/model/FilterHelper';
import { Settings } from '~modules/media-library/model/Settings';
import type { SortOption, SortOptionMap } from '~modules/media-library/model/SortOption';
import { SortOrder } from '~modules/media-library/model/SortOrder';
import { UserPreferences } from '~modules/media-library/model/UserPreferences';
import { ViewVariant } from '~modules/media-library/model/ViewVariant';

export type SortOptionDto = {
  field: string;
  label: string;
  asc: string;
  desc: string;
  default: 'asc' | 'desc';
};

export const SortOptionDtoValidator: Describe<SortOptionDto> = type({
  field: string(),
  label: string(),
  asc: string(),
  desc: string(),
  default: defaulted(enums(Object.values(SortOrder).map(v => v as 'asc' | 'desc')), SortOrder.ASC),
});

export type UserPreferencesDto = {
  filters: Record<string, string[]>;
  tab_filter: Record<string, string> | undefined;
  sort: string;
  order: 'asc' | 'desc';
  result_view: ResultViewVariant | undefined;
};

export const UserPreferencesDtoValidator: Describe<UserPreferencesDto> = type({
  filters: defaulted(record(string(), array(string())), {}),
  tab_filter: optional(record(string(), string())),
  sort: coerce(string(), any(), val => (!!val ? String(val) : '')),
  order: defaulted(enums(Object.values(SortOrder).map(v => v as 'asc' | 'desc')), SortOrder.ASC),
  result_view: defaulted(enums(Object.values(ViewVariant).map(v => v as ResultViewVariant)), ViewVariant.DEFAULT),
});

export type ResultViewVariant = 'list' | 'tile-small' | 'tile-medium' | 'tile-large';

export type ResultViewsDto = Array<ResultViewVariant>;

export type SettingsDto = {
  filters: FilterDto[];
  tab_filter: FilterDto | undefined;
  sort_types: SortOptionDto[];
  user_preferences: UserPreferencesDto;
  result_views: ResultViewsDto;
  additional_settings: any;
};

export const SettingsDtoValidator: Describe<SettingsDto> = type({
  filters: array(FilterDtoValidator),
  tab_filter: optional(FilterDtoValidator),
  sort_types: array(SortOptionDtoValidator),
  user_preferences: UserPreferencesDtoValidator,
  result_views: array(enums(Object.values(ViewVariant) as SettingsDto['result_views'])),
  additional_settings: defaulted(optional(object()), {}),
});

function makeSortOption(rawSortOption: SortOptionDto): SortOption {
  return {
    key: rawSortOption.field,
    label: rawSortOption.label,
    asc: rawSortOption.asc,
    desc: rawSortOption.desc,
    default: rawSortOption.default as SortOrder,
  };
}

function makeSortOptionMap(rawSortOptions: SortOptionDto[] = []) {
  const map: SortOptionMap = new Map();
  rawSortOptions.forEach(rawSortOption => {
    const sortOption = makeSortOption(rawSortOption);
    map.set(sortOption.key, sortOption);
  });
  return map;
}

function makePreferences(rawPreferences: UserPreferencesDto, defaults: { defaultSortKey: string }): UserPreferences {
  const activeFilterIds = Object.keys(rawPreferences.filters);
  const selectedFilterValueIds: UserPreferences['selectedFilterValueIds'] = [];
  Object.entries(rawPreferences.filters).forEach(([filterKey, values]) => {
    values.forEach(value => {
      selectedFilterValueIds.push(makeFilterOptionId(filterKey, value));
    });
  });

  let selectedTabFilterId = '';
  if (Boolean(rawPreferences.tab_filter)) {
    const tabKey = Object.keys(rawPreferences.tab_filter)[0];
    const tabValue = rawPreferences.tab_filter[tabKey];
    selectedTabFilterId = makeFilterOptionId(tabKey, tabValue);
  }

  return {
    activeFilterIds,
    selectedFilterValueIds,
    selectedTabFilterId: selectedTabFilterId,
    sortKey: rawPreferences.sort || defaults.defaultSortKey,
    sortOrder: rawPreferences.order as SortOrder,
    viewVariant: rawPreferences.result_view as ViewVariant,
  };
}

function makeViewVariants(rawResultViews: ResultViewsDto): ViewVariant[] {
  return rawResultViews.map(rawResultView => rawResultView as ViewVariant).filter(Boolean);
}

export function makeSettings(raw: unknown): Settings {
  const settings = create(raw, SettingsDtoValidator);

  const sortOptions = makeSortOptionMap(settings.sort_types);
  const defaultSortKey = settings.sort_types?.[0]?.field;
  return {
    filters: makeFilterList(settings.filters),
    tabFilter: settings.tab_filter !== null ? makeTabFilter(settings.tab_filter) : null,
    sortOptions,
    preferences: makePreferences(settings.user_preferences, { defaultSortKey }),
    viewVariants: makeViewVariants(settings.result_views),
    additionalSettings: settings.additional_settings,
  };
}
